import { useTranslation } from "react-i18next";
import { useParallax } from "@/hooks/useParallax";
import ScrollReveal from "@/components/feature/ScrollReveal";
import CountUp from "@/components/feature/CountUp";

interface Step {
  icon: string;
  titleKey: string;
  descKey: string;
  metaKey: string;
  unitKey: string;
  value: number;
}

const steps: Step[] = [
  { icon: "ri-compass-3-line", titleKey: "cs.process.step1.title", descKey: "cs.process.step1.desc", metaKey: "cs.process.step1.meta", unitKey: "cs.process.step1.statUnit", value: 3 },
  { icon: "ri-radar-line", titleKey: "cs.process.step2.title", descKey: "cs.process.step2.desc", metaKey: "cs.process.step2.meta", unitKey: "cs.process.step2.statUnit", value: 365 },
  { icon: "ri-search-eye-line", titleKey: "cs.process.step3.title", descKey: "cs.process.step3.desc", metaKey: "cs.process.step3.meta", unitKey: "cs.process.step3.statUnit", value: 60 },
  { icon: "ri-shield-flash-line", titleKey: "cs.process.step4.title", descKey: "cs.process.step4.desc", metaKey: "cs.process.step4.meta", unitKey: "cs.process.step4.statUnit", value: 5 },
  { icon: "ri-line-chart-line", titleKey: "cs.process.step5.title", descKey: "cs.process.step5.desc", metaKey: "cs.process.step5.meta", unitKey: "cs.process.step5.statUnit", value: 52 },
];

export default function Proceso() {
  const { t } = useTranslation("common");
  const { ref: bgRef, offset } = useParallax(0.06);

  return (
    <section id="proceso" className="relative py-16 md:py-24 bg-background-950 overflow-hidden">
      {/* Humanized background with subtle parallax */}
      <div ref={bgRef} className="absolute inset-0 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Cybersecurity%20team%20gathered%20around%20a%20planning%20board%20mapping%20an%20incident%20response%20process%2C%20professionals%20collaborating%20with%20sticky%20notes%20and%20diagrams%2C%20warm%20human%20atmosphere%20in%20a%20modern%20office%2C%20professional%20editorial%20photography%2C%20human%20centered%2C%20soft%20depth%20of%20field&width=900&height=506&seq=cs-process-human-bg-01&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="absolute -top-[10%] left-0 w-full h-[120%] object-cover object-center opacity-[0.12]"
          style={{ transform: `translateY(${offset}px)` }}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950 via-background-950/40 to-background-950" />
      </div>
      <div className="absolute top-0 left-0 right-0 h-24 md:h-32 bg-gradient-to-b from-background-950 to-transparent z-[5] pointer-events-none" />

      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage:
            "linear-gradient(oklch(var(--primary-500) / 0.3) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.3) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10">
        <ScrollReveal className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent-400/20 bg-accent-500/8 mb-4">
            <i className="ri-route-line text-accent-300 text-xs" />
            <span className="text-[10px] font-label font-semibold text-accent-300 uppercase tracking-[0.2em]">
              {t("cs.process.tag")}
            </span>
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-50 mb-3 tracking-tight">
            {t("cs.process.title")} <span className="text-accent-400">{t("cs.process.highlight")}</span>
          </h2>
          <p className="text-sm text-foreground-400/60 max-w-xl mx-auto leading-relaxed">
            {t("cs.process.subtitle")}
          </p>
        </ScrollReveal>

        <div className="max-w-6xl mx-auto relative">
          {/* Connecting line (desktop) */}
          <div className="hidden lg:block absolute top-[28px] left-[9%] right-[9%] h-px bg-gradient-to-r from-transparent via-primary-400/30 to-transparent z-0" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-9 lg:gap-6">
            {steps.map((step, idx) => (
              <ScrollReveal
                key={step.titleKey}
                delay={idx * 110}
                className="relative flex flex-col items-center text-center"
              >
                <div className="relative z-10 w-14 h-14 rounded-full bg-background-950 border border-primary-400/25 text-primary-300 flex items-center justify-center mb-5">
                  <i className={`${step.icon} text-xl`} />
                  <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-primary-500 text-white text-[10px] font-heading font-bold flex items-center justify-center">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-100 mb-2 min-h-[2.5rem] flex items-center justify-center">
                  {t(step.titleKey)}
                </h3>
                <p className="text-xs md:text-sm text-foreground-400/60 leading-relaxed mb-4">
                  {t(step.descKey)}
                </p>

                {/* Animated stat */}
                <div className="mb-4">
                  <p className="font-heading font-bold text-3xl md:text-4xl text-primary-300 leading-none">
                    <CountUp end={step.value} />
                  </p>
                  <p className="text-[10px] font-label text-foreground-400/50 uppercase tracking-wider mt-1.5">
                    {t(step.unitKey)}
                  </p>
                </div>

                <span className="mt-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100/10 border border-secondary-400/15 text-[10px] font-label font-semibold text-secondary-200 uppercase tracking-wider">
                  <i className="ri-time-line text-xs" />
                  {t(step.metaKey)}
                </span>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}